import { useEffect, useState } from 'preact/hooks';
import { api } from '../../services/api.js';
import { PaymentPauseNotice } from '../booking/PaymentPauseNotice.js';
import { Button } from '../ui/Button.js';
import { Spinner } from '../ui/Spinner.js';

/** 459: the `qr` shape `payment-url` returns for a DuitNow QR channel
 * (contracts/payment-channel-api.md). `poll_url_token` is Bayarcash's own transaction id — an
 * opaque identifier from this component's perspective, never the provider's real `poll_url` or
 * bearer token. */
export interface QrData {
  qr_image: string;
  qr_string: string;
  poll_url_token: string;
  expires_at: string;
  next_poll_after_ms: number;
}

type PollStatus = 'pending' | 'paid' | 'failed' | 'expired';

interface DuitNowQrPanelProps {
  bookingId: string;
  qr: QrData;
  /** The real settlement already happened via Bayarcash's own callback by the time this fires —
   * this is only the UI's cue to refetch and show the confirmed booking in place. */
  onPaid: () => void;
  /** Requests a fresh code for the same channel (expired or failed) — the confirm page re-calls
   * `payment-url`, which reuses this same attempt where safe (research.md R3). */
  onRegenerate: () => void;
  /** 406 (#1369): payments are paused (the banks' Friday downtime). The panel keeps checking — a code already shown
   * may still be paid — but offers no new code; the pause notice stands where those buttons were. */
  paymentsPaused?: boolean;
}

/** 459 (T022): renders the QR inline, polls `payment-qr-status` at the server-given interval, and
 * reacts to each terminal state. Never settles anything itself — see the endpoint's own contract. */
export function DuitNowQrPanel({
  bookingId,
  qr,
  onPaid,
  onRegenerate,
  paymentsPaused = false,
}: DuitNowQrPanelProps) {
  const [status, setStatus] = useState<PollStatus>('pending');
  // Bumped every tick so the poll effect re-arms even when the read comes back with the SAME
  // status twice in a row — a `status`-only dependency would otherwise poll exactly once
  // (BookingReturnPage's own documented fix for this exact trap).
  const [tick, setTick] = useState(0);

  // `qr.poll_url_token` is a trigger, not a value read in the body — a fresh QR (regenerate)
  // must reset status/tick even though nothing here reads the token itself.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see prose above
  useEffect(() => {
    setStatus('pending');
    setTick(0);
  }, [qr.poll_url_token]);

  // `tick` is a deliberate re-arm signal, not a value read in the body — without it, a poll that
  // lands on the SAME status (`pending` → `pending`) would never re-schedule (069's own trap).
  // biome-ignore lint/correctness/useExhaustiveDependencies: see prose above
  useEffect(() => {
    if (status !== 'pending') return;
    const timer = setTimeout(async () => {
      setTick((t) => t + 1);
      try {
        const result = await api.get<{ status: PollStatus; next_poll_after_ms: number }>(
          `/bookings/${bookingId}/payment-qr-status?token=${qr.poll_url_token}`,
        );
        setStatus(result.status);
      } catch {
        // A transient poll failure just retries on the next tick — it says nothing about whether
        // the payment itself failed, only that this one read did not land.
      }
    }, qr.next_poll_after_ms);
    return () => clearTimeout(timer);
  }, [status, tick, qr.poll_url_token, qr.next_poll_after_ms, bookingId]);

  // Intentionally excludes `onPaid` itself — it is a fresh function identity every render, and
  // depending on it would re-fire this effect (and re-call `onPaid`) on every unrelated re-render
  // while `status` stays 'paid'.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see prose above
  useEffect(() => {
    if (status === 'paid') onPaid();
  }, [status]);

  return (
    <div className="mt-6 bg-surface-card rounded-xl p-4 flex flex-col items-center gap-3">
      <img
        src={qr.qr_image}
        alt="Scan with your banking app to pay"
        data-testid="qr-image"
        className="w-48 h-48 rounded-lg bg-white p-2"
      />
      {status === 'pending' && (
        <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="qr-pending">
          <Spinner size="sm" />
          Waiting for payment…
        </div>
      )}
      {status === 'expired' && (
        <div className="flex flex-col items-center gap-2" data-testid="qr-expired">
          <p className="text-sm text-yellow-400">This code has expired.</p>
          {paymentsPaused ? (
            <PaymentPauseNotice className="text-center" />
          ) : (
            <Button data-testid="qr-regenerate" size="sm" onClick={onRegenerate}>
              Get a new code
            </Button>
          )}
        </div>
      )}
      {status === 'failed' && (
        <div className="flex flex-col items-center gap-2" data-testid="qr-failed">
          <p className="text-sm text-red-400">Payment could not be completed.</p>
          {paymentsPaused ? (
            <PaymentPauseNotice className="text-center" />
          ) : (
            <Button data-testid="qr-retry" size="sm" onClick={onRegenerate}>
              Try again
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
